import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/config';
import { REFERRALS_ENABLED } from '../utils/featureFlags';

const API = API_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Fetch user with explicit token (used after login/register when state hasn't updated yet)
  const fetchUserWithToken = async (authToken) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, {
      email,
      password
    });
    const { access_token } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    // Fetch user with the new token directly (state update is async)
    await fetchUserWithToken(access_token);
    return response.data;
  };

  const register = async (name, email, password, pendingCampaign = null) => {
    const payload = {
      name,
      email,
      password
    };
    
    // Include pending campaign data if provided
    if (pendingCampaign) {
      payload.pending_campaign = pendingCampaign;
    }
    
    // Include referral code from localStorage if available — only when feature is enabled
    const storedRefCode = REFERRALS_ENABLED ? localStorage.getItem('fth_referral_code') : null;
    if (storedRefCode) {
      payload.referral_code = storedRefCode;
    }
    
    const response = await axios.post(`${API}/auth/register`, payload);
    const { access_token, requires_email_verification } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    // Fetch user with the new token directly (state update is async)
    await fetchUserWithToken(access_token);
    
    // After registration, attach referral to user via API
    if (storedRefCode && access_token) {
      try {
        await axios.post(`${API}/referrals/attach`, 
          { referral_code: storedRefCode },
          { headers: { Authorization: `Bearer ${access_token}` } }
        );
        // Clear the referral code after successful attachment
        localStorage.removeItem('fth_referral_code');
        localStorage.removeItem('fth_referral_expiry');
      } catch (err) {
        // Don't block registration if referral attach fails
        console.warn('Referral attach failed:', err.response?.data?.detail || err.message);
      }
    }
    
    return { ...response.data, requires_email_verification: true };
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  // Update campaign preferences
  const updateCampaignPreferences = async (category, subcategory) => {
    try {
      const response = await axios.put(
        `${API}/auth/campaign-preferences`,
        { preferred_category: category, preferred_subcategory: subcategory },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update local user state with new preferences
      setUser(prev => ({
        ...prev,
        preferred_category: category,
        preferred_subcategory: subcategory
      }));
      return response.data;
    } catch (error) {
      console.error('Failed to update campaign preferences:', error);
      throw error;
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    fetchUser,
    refreshUser: fetchUser,
    updateCampaignPreferences
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
